import { Mutation, VuexModule, Module, Action, getModule } from 'vuex-module-decorators'
import { getCookie, ApiAgent } from '@/plugins/ApiRequest'
import * as ApiPath from '@/tcw/constant/ApiPath'
import { UserSession, LoginResponse, KintaiConfig, TcwRole, LoginRequest } from '@/tcw/api/types'
import * as tcw from '@/tcw/utils/TcwService'
import store from '@/store'
import { projectStore } from '@/store/session/ProjectStoreModule'
import { codeLabelStore } from '@/store/session/CodeLabelStoreModule'
import { shouninTaskStore } from '@/store/session/ShouninTaskStoreModule'

/**
 * メッセージSTORE
 */
@Module({
  name: 'SessionStoreModule',
  store,
  namespaced: true,
  dynamic: true
})
export default class SessionStoreModule extends VuexModule {
  private token: string | null = null
  private userInfo: UserSession | null = null
  private roleInfo: TcwRole = 'GUEST'
  private kintaiConfigSession: KintaiConfig | null = null

  /****************
   * Mutation
   ****************/
  @Mutation
  private setUserInfo(userInfo: UserSession | null) {
    this.userInfo = userInfo
  }
  @Mutation
  private setToken(token: string | null) {
    this.token = token
  }
  @Mutation
  private setKintaiConfig(kintaiConfig: KintaiConfig | null) {
    this.kintaiConfigSession = kintaiConfig
  }
  @Mutation
  private setRole(role: TcwRole) {
    this.roleInfo = role
  }

  /****************
   * Getter
   ****************/
  public get has(): boolean {
    return this.userInfo !== null && this.userInfo.id !== null
  }
  public get user(): UserSession | null {
    return this.userInfo
  }
  public get kintaiConfig(): KintaiConfig | null {
    return this.kintaiConfigSession
  }
  public get sessionToken(): string {
    return this.token !== null ? this.token : ''
  }
  public get role(): TcwRole {
    return this.roleInfo
  }

  /****************
   * Action
   ****************/
  /**
   * ログイン
   */
  @Action({ rawError: true })
  public async login(params: LoginRequest): Promise<void> {
    try {
      const url = ApiPath.Paths.Login.get
      const data = new FormData()
      data.append('loginId', params.loginId)
      data.append('password', params.password)
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), this.sessionToken)
      const response: LoginResponse = await apiAgent.call<LoginResponse>(url, 'POST', data)
      this.setSession(response)
    } catch (e) {
      console.error('ログインに失敗しました')
      throw e
    }
  }
  /**
   * ログイン
   */
  @Action({ rawError: true })
  public async fetch(): Promise<string> {
    try {
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), this.sessionToken)
      const response: LoginResponse = await apiAgent.call<LoginResponse>(
        ApiPath.Paths.Session.get,
        'GET'
      )
      this.setSession(response)

      return Promise.resolve('')
    } catch (e) {
      return Promise.reject(e)
    }
  }
  @Action({ rawError: true })
  public setSession(sessionData: LoginResponse): void {
    // ユーザ情報取り出し
    this.setUserInfo(sessionData.userInfo)
    console.log('プロジェクト情報取得')
    projectStore.set(sessionData.projectSettei)
    if (sessionData.codeLabel) {
      console.log('コードラベル情報セット')
      codeLabelStore.set(sessionData.codeLabel)
    }
    if (sessionData.shouninTasks) {
      console.log('承認タスク情報セット')
      shouninTaskStore.set(sessionData.shouninTasks)
    }
    if (sessionData.kintaiConfig) {
      console.log('勤怠コンフィグセット')
      this.setKintaiConfig(sessionData.kintaiConfig)
    }
    console.log('ロールセット')
    this.setRole(sessionData.role)

    // トークン取り出し
    const token = getCookie('_ctkn')
    this.setToken(token)
  }

  /**
   * エラー除去
   */
  @Action({ rawError: true })
  public async logout(): Promise<void> {
    const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), this.sessionToken)
    await apiAgent.call(ApiPath.Paths.Login.delete, 'POST')

    // ストアをクリアする
    this.clear()
  }
  /**
   * エラー除去
   */
  @Action({ rawError: true })
  public async clear(): Promise<void> {
    this.setUserInfo(null)
    this.setToken(null)
    this.setKintaiConfig(null)
    projectStore.clear()
    codeLabelStore.clear()
    shouninTaskStore.clear()
  }
}

export const sessionStore = getModule(SessionStoreModule)
